import axios from 'axios'
import { getAccessToken, getRefreshToken, removeTokens, setAccessToken } from '../storage/auth'
import { decodeJwtClaims } from './jwt'
import { loginUrlFor, refreshEndReason, sessionEndReason, SSO_REQUIRED, stashLogoutMessage, stashLogoutReason } from '../context/logout-destination'
import DeviceDetector from "device-detector-js";
import { runInBackground, markBackground } from './background-flag'
import { carryRequestID, tagRequestID } from './error/request-id'

// Redirect to the login screen after a session ends. Preserves the current page
// and its query (base64-encoded to match useLoginRedirect's atob reader) so re-login returns
// the user where they were, and flags the reason so the login screen can explain
// the sign-out.
const redirectToLogin = () => {
  const path = `${window.location.pathname}${window.location.search || ''}`
  const params = new URLSearchParams()
  if (path && !path.startsWith('/auth')) {
    params.set('redirect', btoa(path))
  }
  params.set('reason', 'inactivity')
  window.location.replace(`/auth/email?${params.toString()}`)
}

// Require SSO ended the session (logout-destination.js SSO_REQUIRED): auth
// answers 401 { message, reason: 'sso_required' } on the refresh (here and
// useRefresh), the organization switch (PUT /auth-organization), a launch
// (POST /launch) and a device approval (PUT /auth-device), having ended the
// session itself. Whichever call hears it, the session ends here as a
// refused refresh does -- tokens gone, the reason and auth's sentence
// stashed (never in the address) -- and the browser goes to /auth/email,
// which shows the sentence over the email step and offers the SSO button as
// usual. true when it did.
export const endSessionIfSSORequired = error => {
  if (sessionEndReason(error) !== SSO_REQUIRED) return false
  removeTokens()
  stashLogoutReason(SSO_REQUIRED)
  stashLogoutMessage(error)
  window.location.replace(loginUrlFor(`${window.location.pathname}${window.location.search || ''}`, SSO_REQUIRED))
  return true
}

const checkAccessTokenExp = async accessToken => {

  const claimsObj = decodeJwtClaims(accessToken)
  if (!claimsObj) {
    removeTokens()
    redirectToLogin()
    return accessToken
  }

  let now = new Date()

  let refreshExp = new Date(claimsObj.refreshTokenExp * 1000)
  let accessExp = new Date(claimsObj.exp * 1000)


  if (now > refreshExp) {
    removeTokens()
    redirectToLogin()
  }

  if (now > accessExp) {

    let refreshToken = getRefreshToken()
    let endpoint = `${process.env.REACT_APP_API_AUTH}/api/v1/auth-refresh/${refreshToken}`

    try {
      let result = await axios.get(endpoint)

      if (result.status === 200) {
        accessToken = result.data.accessToken
        // A bounded LMS session's token is never stored (storage/auth.js): a
        // refresh that hands one back cannot keep this session.
        if (setAccessToken(accessToken) === false) {
          removeTokens()
          redirectToLogin()
        }
      } else if (result.status === 401) {
        removeTokens()
        redirectToLogin()
      } else {
        removeTokens()
        redirectToLogin()
      }
    } catch (exp) {
      // The server ends admin sessions itself once they've gone idle past
      // ADMIN_IDLE_TIMEOUT or aged past ADMIN_SESSION_MAX, answering this
      // refresh with 401 { message, reason: 'idle' | 'max' } and the refresh
      // token already revoked. Nothing is left to log out server-side --
      // just drop the local session and send the browser back to where it
      // was, flagged with the reason so the login screen can explain it.
      // Any other 401 (or a network failure) keeps the prior generic
      // inactivity redirect.
      if (endSessionIfSSORequired(exp)) return accessToken

      const reason = refreshEndReason(exp)

      if (reason) {
        removeTokens()
        window.location = loginUrlFor(`${window.location.pathname}${window.location.search || ''}`, reason)
      } else {
        removeTokens()
        redirectToLogin()
      }
    }
  }

  return accessToken
}

// Refresh the access token if expired, using the same pre-request check the
// axios layer runs. Exported for upload paths (Uppy XHR) that bypass axios.
export const ensureFreshAccessToken = async () => checkAccessTokenExp(getAccessToken())

// The edxr-user-agent header every request sends (JSON), or null when the
// browser gives no usable user agent. Also sent with browser error reports
// (error/client-reporter.js), so content names their app and browser.
export const edxrUserAgentHeader = () => {
  //some browsers might not have useragent or proper device info
  try {
    const deviceDetector = new DeviceDetector();
    const info = deviceDetector.parse(window.navigator.userAgent);
    // `app` names THIS client for sessions/devices displays (admin + auth's
    // ClientLabel compose it verbatim when it isn't 'viewer'/'editor' -- see
    // device-identity.js's APP_DISPLAY and rapid-api-auth's appDisplayName).
    // It used to be the generic 'web', indistinguishable from any other
    // browser-based client and rendered there as a bare "web 1.0.3" / "Web
    // Browser" reading rather than identifying the admin panel by name.
    let edxrUserAgent = { app: 'Admin Panel', appVersion: process.env.REACT_APP_VERSION, deviceName: info.device.brand,  deviceType: info.device.type, deviceOS: info.os.name, devicePlatform: info.client.name }
    return JSON.stringify(edxrUserAgent)
  } catch {
    return null
  }
}

const getConfig = async (config, contentType = 'application/json') => {
  config = config || {}

  let headers = {}

  let accessToken = getAccessToken()

  if (accessToken) {
    accessToken = await checkAccessTokenExp(accessToken)
    headers['Authorization'] = accessToken
  }

  headers['Content-Type'] = contentType


  const edxrUserAgent = edxrUserAgentHeader()
  if (edxrUserAgent) headers['edxr-user-agent'] = edxrUserAgent


  config['headers'] = headers

  return config
}

const getConfigBlob = () => {
  let config = {
    responseType: 'blob'
  }

  let headers = {}

  const accessToken = getAccessToken()

  if (accessToken) {
    headers['Authorization'] = accessToken
  }

  config['headers'] = headers

  return config
}

// function sleep(ms) {
//   console.log('DELAYING API FOR TESTING MS: ', ms)
//   return new Promise(resolve => setTimeout(resolve, ms))
// }

// function checkAuthRedirect(error) {
//   if (error.response.status === 401) {
//     window.location = '/auth/email'
//   }

//   // if (error.message === 'Request failed with status code 401') {
//   //   window.location = '/auth/email'
//   // }
// }

function checkError(error, fnReject, { expected = false } = {}) {
  tagRequestID(error, { expected })
  if (error.response) {
    if (error.response.data.error) {
      fnReject(carryRequestID({ error: error.response.data.error, status: error.response.status }, error))
    } else if (error.response.data) {
      fnReject(carryRequestID({ error: error.response.data, status: error.response.status }, error))
    } else {
      fnReject(carryRequestID({ error: 'An error occurred.' }, error))
    }
  } else if (error.request) {
    fnReject(error.request)
  } else {
    fnReject({ error: 'An error occurred.' })
  }
}


// runInBackground/markBackground: see ./background-flag.js. Re-exported here
// so table-ag-client.js's existing `import { runInBackground } from
// '../api.service'` keeps working unchanged.
export { runInBackground }

// A failure rejects with axios's error, tagged with the failed request's id
// (X-Request-ID) on the error, its response and its body (error/request-id):
// every layer above hands one of those on, and the error's display shows the
// id as the reference for support. A success is returned untouched.
const tagged = async call => {
  try {
    return await call()
  } catch (error) {
    endSessionIfSSORequired(error)
    throw tagRequestID(error)
  }
}

export const getAsync = async (server, endpoint, data) => {
  const url = markBackground(`${server}/${endpoint}`)
  let config = await getConfig()
  return await tagged(() => axios.get(url, config))
}


export const postAsync = async (server, endpoint, data) => {
  const url = `${server}/${endpoint}`
  let config = await getConfig()
  return await tagged(() => axios.post(url, data, config))
}

export const putAsync = async (server, endpoint, data) => {
  const url = `${server}/${endpoint}`
  let config = await getConfig()
  return await tagged(() => axios.put(url, data, config))
}

export const patchAsync = async (server, endpoint, data) => {
  const url = `${server}/${endpoint}`
  let config = await getConfig()
  return await tagged(() => axios.patch(url, data, config))
}

// data: an optional JSON body (a revoke's reason); axios sends a DELETE's
// body from the config.
export const deleteAsync = async (server, endpoint, data) => {
  const url = `${server}/${endpoint}`
  let config = await getConfig()
  if (data !== undefined) config = { ...config, data }
  return await tagged(() => axios.delete(url, config))
}

/////////////////////////////////////////
/////////////////////////////////////////
/////////////////////////////////////////
/////////////////////////////////////////

// quietNotFound: a 404 is an answer the caller expects (storage's "nothing
// has been uploaded for this file yet"), so it isn't written to the console
// and isn't the page's last failed request (a browser error report never
// names it, error/request-id.js); it still rejects as any failure does.
export function getBlobAsync(server, endpoint, { quietNotFound = false } = {}) {
  return new Promise((resolve, reject) => {
    const url = `${server}/${endpoint}`
    let config = getConfigBlob()

    axios
      .get(url, config)
      .then(async response => {
        resolve(response)
      })
      .catch(error => {
        const expected = Boolean(quietNotFound && error && error.response && error.response.status === 404)
        if (!expected) console.error(error)
        checkError(error, reject, { expected })
      })
  })
}

// export function getAsync(server, endpoint) {
//   return new Promise(async (resolve, reject) => {
//     const url = `${server}/${endpoint}`

//     let config = await getConfig()

//     axios
//       .get(url, config)
//       .then(async response => {
//         // if (process.env.REACT_APP_API_DELAY) {
//         //   await sleep(process.env.REACT_APP_API_DELAY)
//         // }

//         resolve(response)
//       })
//       .catch(error => {
//         console.error(error)
//         checkAuthRedirect(error)
//         checkError(error, reject)
//       })
//   })
// }

// export function postAsync(server, endpoint, data) {
//   return new Promise(async (resolve, reject) => {
//     const url = `${server}/${endpoint}`

//     let config = await getConfig()

//     axios
//       .post(url, data, config)
//       .then(async response => {
//         // if (process.env.REACT_APP_API_DELAY) {
//         //   await sleep(process.env.REACT_APP_API_DELAY)
//         // }

//         resolve(response)
//       })
//       .catch(error => {
//         console.error(error)
//         checkAuthRedirect(error)
//         checkError(error, reject)
//       })
//   })
// }

// export function putAsync(server, endpoint, data) {
//   return new Promise(async (resolve, reject) => {
//     const url = `${server}/${endpoint}`

//     let config = await getConfig()

//     axios
//       .put(url, data, config)
//       .then(async response => {
//         // if (process.env.REACT_APP_API_DELAY) {
//         //   await sleep(process.env.REACT_APP_API_DELAY)
//         // }

//         resolve(response)
//       })
//       .catch(error => {
//         console.error(error)
//         checkAuthRedirect(error)
//         checkError(error, reject)
//       })
//   })
// }

// export function deleteAsync(server, endpoint) {
//   return new Promise(async (resolve, reject) => {
//     const url = `${server}/${endpoint}`

//     let config = await getConfig()

//     axios
//       .delete(url, config)
//       .then(async response => {
//         // if (process.env.REACT_APP_API_DELAY) {
//         //   await sleep(process.env.REACT_APP_API_DELAY)
//         // }

//         resolve(response)
//       })
//       .catch(error => {
//         console.error(error)
//         checkAuthRedirect(error)
//         checkError(error, reject)
//       })
//   })
// }
